import React, { useCallback, useEffect, useRef, useState } from "react";
import { MessageCircle, X, Plus, ChevronLeft, Paperclip, FileText, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { SupportChatThread, uploadSupportFile, fmtTime } from "@/components/support/SupportChatThread";

export const CATEGORIES = [
    { v: "booking", l: "Rezervasyon" },
    { v: "pricing", l: "Fiyat / Takvim" },
    { v: "payment", l: "Ödeme" },
    { v: "technical", l: "Teknik" },
    { v: "other", l: "Diğer" },
];
export const STATUS = {
    open: { l: "Açık", c: "bg-sky-100 text-sky-700 dark:bg-sky-950/40 dark:text-sky-300" },
    pending: { l: "Bekliyor", c: "bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300" },
    resolved: { l: "Çözüldü", c: "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300" },
};

const INPUT = "w-full px-3 py-2 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-sm text-slate-900 dark:text-white outline-none";

function BookingPicker({ value, onChange }) {
    const [q, setQ] = useState("");
    const [items, setItems] = useState([]);
    useEffect(() => {
        const t = setTimeout(() => {
            api.get("/hotel/bookings", { params: { q, page_size: 10 } }).then((r) => setItems(r.data.items || [])).catch(() => setItems([]));
        }, 300);
        return () => clearTimeout(t);
    }, [q]);
    if (value) {
        return (
            <div className="flex items-center justify-between px-3 py-2 rounded-xl bg-sky-50 dark:bg-sky-950/30 border border-sky-200 dark:border-sky-900 text-sm" data-testid="support-booking-selected">
                <span className="font-semibold text-slate-800 dark:text-slate-100">{value.voucher_no || value.id.slice(0, 8).toUpperCase()} · {value.guest?.first_name} {value.guest?.last_name}</span>
                <button onClick={() => onChange(null)} className="p-1"><X className="h-3.5 w-3.5" /></button>
            </div>
        );
    }
    return (
        <div>
            <input data-testid="support-booking-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Voucher no veya misafir adı ile ara (opsiyonel)" className={INPUT} />
            {q && (
                <div className="mt-1 max-h-36 overflow-y-auto rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900">
                    {items.length === 0 && <div className="px-3 py-2 text-xs text-slate-500">Sonuç yok</div>}
                    {items.map((b) => (
                        <button key={b.id} type="button" data-testid={`support-booking-option-${b.id}`} onClick={() => onChange(b)} className="w-full text-left px-3 py-2 text-xs hover:bg-slate-50 dark:hover:bg-slate-800">
                            <b>{b.voucher_no || b.id.slice(0, 8).toUpperCase()}</b> · {b.guest?.first_name} {b.guest?.last_name} · {b.check_in} → {b.check_out}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}

function NewTicketForm({ onCreated }) {
    const [form, setForm] = useState({ category: "booking", subject: "", text: "" });
    const [booking, setBooking] = useState(null);
    const [files, setFiles] = useState([]);
    const [busy, setBusy] = useState(false);
    const fileRef = useRef(null);

    const onPick = async (e) => {
        const list = Array.from(e.target.files || []);
        e.target.value = "";
        setBusy(true);
        try {
            for (const f of list) {
                const up = await uploadSupportFile(f);
                setFiles((p) => [...p, up]);
            }
        } catch (err) {
            toast.error(err.response?.data?.detail || "Dosya yüklenemedi");
        } finally { setBusy(false); }
    };

    const submit = async () => {
        if (form.subject.trim().length < 2) { toast.error("Lütfen bir başlık yazın"); return; }
        if (!form.text.trim() && !files.length) { toast.error("Lütfen mesajınızı yazın"); return; }
        setBusy(true);
        try {
            const r = await api.post("/support/tickets", { ...form, booking_id: booking?.id || null, attachments: files });
            toast.success("Talebiniz iletildi");
            onCreated(r.data);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Talep oluşturulamadı");
        } finally { setBusy(false); }
    };

    return (
        <div className="p-4 space-y-3 overflow-y-auto" data-testid="support-new-form">
            <div>
                <div className="text-[11px] font-bold uppercase tracking-widest text-slate-500 mb-1.5">Konu</div>
                <div className="flex flex-wrap gap-1.5">
                    {CATEGORIES.map((c) => (
                        <button key={c.v} type="button" data-testid={`support-cat-${c.v}`} onClick={() => setForm({ ...form, category: c.v })} className={`px-3 py-1.5 rounded-full text-xs font-bold transition-colors ${form.category === c.v ? "bg-sky-600 text-white" : "bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-200"}`}>{c.l}</button>
                    ))}
                </div>
            </div>
            <input data-testid="support-subject" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} placeholder="Başlık (ör. 17 Ekim fiyatı görünmüyor)" className={INPUT} />
            <div>
                <div className="text-[11px] font-bold uppercase tracking-widest text-slate-500 mb-1.5">İlgili Rezervasyon</div>
                <BookingPicker value={booking} onChange={setBooking} />
            </div>
            <textarea data-testid="support-first-message" value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })} rows={4} placeholder="Mesajınız…" className={`${INPUT} resize-none`} />
            <div className="flex flex-wrap items-center gap-1.5">
                <input ref={fileRef} type="file" multiple hidden onChange={onPick} data-testid="support-new-file-input" accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt" />
                <button type="button" onClick={() => fileRef.current?.click()} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300"><Paperclip className="h-3.5 w-3.5" /> Dosya / görsel ekle</button>
                {files.map((f) => <span key={f.url} className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-slate-100 dark:bg-slate-800 text-[11px]"><FileText className="h-3 w-3" />{f.name}</span>)}
            </div>
            <button data-testid="support-create-btn" onClick={submit} disabled={busy} className="w-full py-2.5 rounded-xl bg-sky-600 hover:bg-sky-700 text-white text-sm font-bold disabled:opacity-60 inline-flex items-center justify-center gap-2">
                {busy && <Loader2 className="h-4 w-4 animate-spin" />} Talebi Gönder
            </button>
        </div>
    );
}

function TicketList({ items, onOpen }) {
    if (!items.length) return <div className="p-6 text-center text-sm text-slate-500">Henüz destek talebiniz yok. Yeni bir talep açarak bize yazabilirsiniz.</div>;
    return (
        <div className="overflow-y-auto divide-y divide-slate-100 dark:divide-slate-800">
            {items.map((t) => (
                <button key={t.id} data-testid={`support-ticket-${t.id}`} onClick={() => onOpen(t)} className="w-full text-left px-4 py-3 hover:bg-slate-50 dark:hover:bg-slate-800/50">
                    <div className="flex items-center gap-2">
                        <span className="text-sm font-bold text-slate-900 dark:text-white truncate flex-1">#{t.number} · {t.subject}</span>
                        {t.unread > 0 && <span className="h-5 min-w-5 px-1.5 rounded-full bg-rose-500 text-white text-[10px] font-bold flex items-center justify-center">{t.unread}</span>}
                    </div>
                    <div className="flex items-center gap-2 mt-1">
                        <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${STATUS[t.status]?.c}`}>{STATUS[t.status]?.l}</span>
                        <span className="text-[11px] text-slate-500 truncate flex-1">{t.last_message_preview}</span>
                        <span className="text-[10px] text-slate-400">{fmtTime(t.last_message_at)}</span>
                    </div>
                </button>
            ))}
        </div>
    );
}

export function SupportWidget() {
    const [open, setOpen] = useState(false);
    const [view, setView] = useState("list");
    const [items, setItems] = useState([]);
    const [active, setActive] = useState(null);
    const [unread, setUnread] = useState(0);

    const loadList = useCallback(() => api.get("/support/tickets").then((r) => setItems(r.data.items || [])).catch(() => {}), []);
    useEffect(() => {
        const tick = () => { if (!document.hidden) api.get("/support/unread").then((r) => setUnread(r.data.unread_messages || 0)).catch(() => {}); };
        tick();
        const t = setInterval(tick, 10000);
        return () => clearInterval(t);
    }, []);
    useEffect(() => { if (open && view === "list") loadList(); }, [open, view, loadList]);

    const openTicket = (t) => { setActive(t); setView("thread"); };

    return (
        <>
            <button data-testid="support-bubble" onClick={() => setOpen((o) => !o)} className="fixed bottom-5 right-5 z-50 h-14 w-14 rounded-full bg-sky-600 hover:bg-sky-700 text-white shadow-xl flex items-center justify-center transition-transform hover:scale-105" title="Destek">
                {open ? <X className="h-6 w-6" /> : <MessageCircle className="h-6 w-6" />}
                {!open && unread > 0 && <span data-testid="support-bubble-unread" className="absolute -top-1 -right-1 h-6 min-w-6 px-1.5 rounded-full bg-rose-500 text-white text-xs font-bold flex items-center justify-center ring-2 ring-white">{unread}</span>}
            </button>
            {open && (
                <div data-testid="support-panel" className="fixed bottom-24 right-5 z-50 w-[min(400px,calc(100vw-24px))] h-[min(600px,calc(100vh-130px))] bg-white dark:bg-slate-900 rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-800 flex flex-col overflow-hidden">
                    <div className="px-4 py-3 bg-sky-600 text-white flex items-center gap-2">
                        {view !== "list" && <button data-testid="support-back" onClick={() => { setView("list"); setActive(null); }} className="p-1 -ml-1 rounded hover:bg-white/15"><ChevronLeft className="h-5 w-5" /></button>}
                        <div className="flex-1 min-w-0">
                            <div className="text-sm font-bold truncate">{view === "thread" && active ? `#${active.number} · ${active.subject}` : view === "new" ? "Yeni Destek Talebi" : "msonlinetravel Destek"}</div>
                            <div className="text-[11px] opacity-80 truncate">{view === "thread" && active ? `${active.category_label}${active.booking_code ? ` · Rez. ${active.booking_code}` : ""} · ${STATUS[active.status]?.l}` : "Size nasıl yardımcı olabiliriz?"}</div>
                        </div>
                        {view === "list" && <button data-testid="support-new-btn" onClick={() => setView("new")} className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-white/15 hover:bg-white/25 text-xs font-bold"><Plus className="h-3.5 w-3.5" /> Yeni Talep</button>}
                    </div>
                    <div className="flex-1 min-h-0 flex flex-col">
                        {view === "list" && <TicketList items={items} onOpen={openTicket} />}
                        {view === "new" && <NewTicketForm onCreated={openTicket} />}
                        {view === "thread" && active && <SupportChatThread ticketId={active.id} side="hotel" onTicket={setActive} />}
                    </div>
                </div>
            )}
        </>
    );
}
